/* eslint-disable import/order */
/* ──────────────────────────────────────────────────────────────
   src/App.tsx – router principal (rutas de profesor refactorizadas)
   ────────────────────────────────────────────────────────────── */
import React, { useMemo, useEffect } from 'react';
import { initEstadosReserva, subscribeEstados } from './constants/enums';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Outlet } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { ToastContainer } from 'react-toastify';
import PlanificadorCentroPage from './pages/admin/manager/centros/PlanificadorCentroPage';
import 'react-toastify/dist/ReactToastify.css';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import 'dayjs/locale/en';
import ProfesorDashboardPage from './pages/profesor/ProfesorDashboardPage';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import baseTheme from './styles/theme';
import { Box } from '@mui/material';
import { esES, enUS } from '@mui/material/locale';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
/* ——— páginas públicas ——— */
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import ConsultaDisponibilidadPage from './pages/admin/ConsultaDisponibilidadPage';
import BonusA365Page from './pages/reports/profesores/BonusA365Page';
/* ——— utilidades ——— */
import ChangePasswordPage from './pages/ChangePasswordPage';
import AusenciasPage from './pages/reports/profesores/AusenciasPage';
import GlobalFirmaDialog from './components/GlobalFirmaDialog';
/* ——— helpers auth / roles ——— */
import ProtectedRoute from './components/ProtectedRoute';
import RequireRole, { createProtectedRoute } from './components/RequireRole';
import RoleLanding from './pages/RoleLanding';
import FormularioFormacion from './pages/admin/FormularioFormacion';
import { useHasDepartmentAccess } from './hooks/useHasDepartmentAccess';
/* ——— CLIENTE ——— */
import ClienteLayout from './pages/cliente/ClienteLayout';
import ClienteAgendaPage from './pages/cliente/ClienteAgendaPage';
import ClienteHistorialPage from './pages/cliente/ClienteHistorialPage';
import ClienteCuentaPage from './pages/cliente/ClienteCuentaPage';
import FirmarPage from './pages/FirmarPage';
import AulasCentroPage from './pages/admin/manager/centros/AulasCentroPage';
/* ——— PROFESOR ——— */
import ProfesorLayout from './pages/profesor/ProfesorLayout';
import ProfesorAgendaPage from './pages/profesor/ProfesorAgendaPage';
import ProfesorHistorialPage from './pages/profesor/ProfesorHistorialPage';
import ProfesorCuentaPage from './pages/profesor/ProfesorCuentaPage';
import MisFirmasPendientes from './pages/profesor/MisFirmasPendientes';
import MorningBlocker from './components/MorningBlocker';
import FormularioBlocker from './components/formularios/FormularioBlocker';
import ProfessorSadWatcher from './components/sad/ProfessorSadWatcher';
import RequesterSadPopup from './components/sad/RequesterSadPopup';
import ModalFollowup from './components/ModalFollowup';
import { SAD_FEATURE_ENABLED } from './config/features';

/* ——— STAFF / ADMIN ——— */
import ReservasCalendarPage from './pages/admin/agenda/ReservasCalendarPage';
import ReservationForm from './components/ReservationForm';
import UsuariosPage from './pages/admin/manager/UsuariosPage';
import AsignaturasPage from './pages/admin/manager/AsignaturasPage';
import CentrosPage from './pages/admin/manager/CentrosPage';
import ClientesPage from './pages/admin/manager/ClientesPage';
import OcupacionProfesorPage from './pages/reports/profesores/OcupacionProfesorPage';
import ProfesorDatosPage from './pages/admin/manager/ProfesorDatosPage'; // ← NUEVO
import ProfesorDisponibilidadPage from './pages/admin/manager/disponibilidad/ProfesorDisponibilidadPage';
import ProfesorVacacionesPage from './pages/admin/manager/ProfesorVacacionesPage';
import ConsultaReservasPendientesPage from './pages/admin/manager/ConsultaReservasPendientes';
import FestivosPage from './pages/admin/manager/FestivosPage';
import ManagerLayout from './pages/admin/manager/ManagerLayout';
import DashboardLayout from './layouts/DashboardLayout';
import DocumentosPage from './pages/admin/manager/documentos/DocumentosPage';
import FormulariosPage from './pages/admin/manager/formularios/FormulariosPage';
import FormulariosMetricasPage from './pages/admin/manager/formularios/FormulariosMetricasPage';
import EvaluacionesMetricasPage from './pages/admin/manager/evaluaciones/EvaluacionesMetricasPage';
import EvaluacionesPendientesPage from './pages/admin/manager/evaluaciones/EvaluacionesPendientesPage';
import UsuarioEvaluacionesPage from './pages/admin/manager/evaluaciones/UsuarioEvaluacionesPage';
import CuadroMensualPage from './pages/admin/guardias/CuadroMensualPage';
import GuardiasCalendarPage from './pages/admin/guardias/GuardiasCalendarPage';
import StaffDisponibilidadPage from './pages/admin/manager/disponibilidad/StaffDisponibilidadPage';
import AuditLogViewer from './pages/admin/AuditLogViewer';
/* ——— INFORMES ——— */
import ReportsRootLayout from './pages/reports/ReportsRootLayaout';
import ProfReportsLayout from './pages/reports/profesores/ProfReportsLayout';
import HorasProfesorPage from './pages/reports/profesores/HorasProfesorPage';
import HorasSemanalesPage from './pages/reports/profesores/HorasProfesoresSemanales';
import HorasProfesoresContratadasPage from './pages/reports/profesores/HorasProfesoresContratadasPage';
import AlumnosProfesorPage from './pages/reports/profesores/AlumnosProfesorPage';
import IncidenciasProfesorPage from './pages/reports/profesores/IncidenciasProfesorPage';
import RetrasosProfesorPage from './pages/reports/profesores/RetrasosProfesorPage';
import OneMinutePaperIncumplimientoPage from './pages/reports/profesores/FirmasIncumplimientoPage';
import AsignaturasProfesorPage from './pages/reports/profesores/AsignaturasProfesorPage';
import VacacionesPage from './pages/reports/profesores/VacacionesPage';
import ClienteReportsLayout from './pages/reports/clientes/ClienteReportsLayout';
import HorasClientesSemanales from './pages/reports/clientes/HorasClientesSemanalesPage';
import ProfesoresAlumnoPage from './pages/reports/clientes/ProfesoresAlumnoPage';
import IncidenciasAlumnoPage from './pages/reports/clientes/IncidenciasAlumnoPage';
import RetrasosAlumnoPage from './pages/reports/clientes/RetrasosAlumnoPage';
import ProgresoAlumnoPage from './pages/reports/clientes/ProgresoClientePage';
import AlumnosProyectoBarPage from './pages/reports/clientes/AlumnosProyectoPage';
import TotalAlumnosUnicosPage from './pages/reports/clientes/TotalAlumnosUnicosPage';
import CancelacionAlumnoPage from './pages/reports/clientes/CancelacionAlumnoPage';
import OneMinutePaperAlumnoPage from './pages/reports/clientes/OneMinutePaperAlumnoPage';
import AltasBajasReport from './pages/reports/clientes/AltasBajasReport';
import HorasClientesMensuales from './pages/reports/clientes/HorasClientesMensuales';
import HeatmapHorarioPage from './pages/reports/profesores/HeatmapHorarioPage';
import TotalesProfesorPage from './pages/reports/profesores/TotalesProfesorPage';
import ReservasReportsLayout from './pages/reports/reservas/ReservasReportsLayout';
import AsignaturasReportsLayout from './pages/reports/asignaturas/AsignaturasReportsLayout';
import ReservasMetricasPage from './pages/reports/reservas/ReservasMetricasPage';
import TotalReservasPage from './pages/reports/reservas/TotalReservasPage';
import CancelacionTasaPage from './pages/reports/reservas/CancelacionTasaPage';
import HeatmapHorarioReservasPage from './pages/reports/reservas/HeatmapHorarioReservasPage';
import DistribucionReservasPage from './pages/reports/reservas/DistribucionReservasPage';
import SeriesReservasPage from './pages/reports/reservas/SeriesReservasPage';
import FirmasDiaPage from './pages/reports/reservas/FirmasDiaPage';
import RetrasosReservasPage from './pages/reports/reservas/RetrasosReservasPage';
import CargaFormatoPage from './pages/reports/reservas/CargaFormatoPage';
import UtilizacionCapacidadPage from './pages/reports/reservas/UtilizacionCapacidadPage';
import IncidenciasTasaPage from './pages/reports/reservas/IncidenciasTasaPage';
import HorasAsignaturasSemanalesPage from './pages/reports/asignaturas/HorasAsignaturasSemanalesPage';
import AsignaturasProfesoresPage from './pages/reports/asignaturas/AsignaturasProfesoresPage';
import AsignaturasDemandaPage from './pages/reports/asignaturas/AsignaturasDemandaPage';
import AsignaturasAlumnosUnicosPage from './pages/reports/asignaturas/AsignaturasAlumnosUnicosPage';
import AsignaturasRatioProfesorPage from './pages/reports/asignaturas/AsignaturasRatioProfesorPage';
import AsignaturasCentroFormatoPage from './pages/reports/asignaturas/AsignaturasCentroFormatoPage';
import { useLocation } from 'react-router-dom';
import FormacionesPage from './pages/reports/profesores/FormacionesPage';
import PrevisionPage from './pages/reports/profesores/PrevisionPage';
import Forbidden from './pages/Forbidden';
import NotificacionesPage from './pages/NotificacionesPage';
import AgendaPage from './pages/admin/agenda/AgendaPage';
import ConsultorLayout from './pages/consultor/ConsultorLayout';
import { useTranslation } from 'react-i18next';
import PublicLayout from './layouts/PublicLayout';
import VacacionesProfesorPage from './pages/profesor/VacacionesProfesorPage';
import DocumentosProfesorPage from './pages/profesor/DocumentosProfesorPage';
import FormFirmaPage from './pages/FormFirmaPage';
import MyForms from './pages/account/MyForms';
import BroadcastModalProvider from './components/BroadcastModalProvider';
import ClienteEvaluacionesReportPage from './pages/reports/clientes/EvaluacionesPage';
import EvaluacionesPage from './pages/admin/manager/evaluaciones/EvaluacionesPage';
import ReservasManagerLayout from './pages/admin/manager/reservas/ReservasManagerLayout';
import ProyectosPage from './pages/admin/manager/reservas/ProyectosPage';
import HighPerformancePage from './pages/reports/profesores/HighPerformancePage';
import SeguimientoServicioPage from './pages/reports/reuniones/SeguimientoServicioPage';
import ReunionesReportsLayout from './pages/reports/reuniones/ReunionesReportsLayout';
import ReunionesMetricasPage from './pages/reports/reuniones/ReunionesMetricasPage';
import CentrosReportsLayout from './pages/reports/centros/CentrosReportsLayout';
import OcupacionCentroPage from './pages/reports/centros/OcupacionCentroPage';
import EstadosReservaPage from './pages/admin/manager/reservas/EstadosReservaPage';
import ReunionReglasPage from './pages/admin/agenda/ReunionReglasPage';
import CalendarioReunionesPage from './pages/admin/agenda/CalendarioReunionesPage';
import MisReunionesPage from './pages/admin/agenda/MisReunionesPage';
//PRUEBAAAAA
import Error404Page from './pages/Error404Page';
import Prueba from './pages/Prueba';

/* ==========  Wrappers utilitarios  ========== */
const ClienteWrapper = () => <ClienteLayout />;
const ProfesorWrapper = () => <ProfesorLayout />;

/* ==========  Página suelta para crear reserva  ========== */
function NuevaReservaPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { initialValues } = (location.state || {}) as any;

  return (
    <ReservationForm
      mode="create"
      initialValues={initialValues}
      onSaved={() => navigate('/agenda')}
      onCancel={() => navigate(-1)}
    />
  );
}

/* ==========  Wrapper protegido que inyecta overlays  ========== */
/* ==========  Wrapper protegido que inyecta overlays  ========== */
function ProtectedOverlays() {
  const { user } = useAuth();
  const { hasAccess } = useHasDepartmentAccess();
  const roles: string[] = user?.roles ?? [];

  const isProfessor = roles.includes('profesor');
  const isSolicitante =
    roles.includes('admin') ||
    hasAccess('hr') ||
    hasAccess('admisiones') ||
    hasAccess('att_familias');

  return (
    <>
      {isProfessor && <MorningBlocker isProfessor />}
      {SAD_FEATURE_ENABLED && isProfessor && <ProfessorSadWatcher isProfesor />}
      {SAD_FEATURE_ENABLED && isSolicitante && <RequesterSadPopup enabled />}
      <FormularioBlocker />
      <ModalFollowup />
      <Outlet />
    </>
  );
}

/* ═════════════════════ COMPONENTE PRINCIPAL ═════════════════════ */
export default function App() {
  const { i18n } = useTranslation<'translation'>();
  const lang = i18n.language.startsWith('en') ? 'en' : 'es';
  const theme = useMemo(() => createTheme(baseTheme, lang === 'es' ? esES : enUS), [lang]);

  const { user, bootstrapped } = useAuth();
  const isAuthenticated = !!user;
  const [, force] = React.useState(0);

  useEffect(() => {
    if (!bootstrapped || !isAuthenticated) return; // ⬅️ espera a que haya sesión lista
    const unsub = subscribeEstados(() => force((n) => n + 1));
    let alive = true;
    (async () => {
      try {
        await initEstadosReserva();
        if (alive) force((n) => n + 1);
      } catch {}
    })();
    return () => {
      alive = false;
      unsub();
    };
  }, [bootstrapped, isAuthenticated, user?.roles?.join(',')]);
  dayjs.locale(lang);

  return (
    <ThemeProvider theme={theme}>
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={lang}>
        {/* 👇 Modal global de BROADCAST: intercepta y muestra en centro de pantalla */}
        <BroadcastModalProvider>
          <BrowserRouter>
            <GlobalFirmaDialog />
            <ToastContainer position="top-center" autoClose={4000} />
            <Routes>
              {/* ─── públicas ─── */}
              <Route element={<PublicLayout />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/firmar" element={<FirmarPage />} />
                <Route path="/form-firma" element={<FormFirmaPage />} />
                <Route path="/mis-documentos" element={<DocumentosProfesorPage />} />

                <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
                <Route path="/403" element={<Forbidden />} />
                <Route path="/404" element={<Error404Page />} />
                <Route path="/forgot-password" element={<ForgotPasswordPage />} />
                <Route path="/sandboxpruebas" element={<Prueba />} />
                <Route path="/notificaciones" element={<NotificacionesPage />} />
              </Route>

              {/* ─── protegidas ─── */}
              <Route element={<ProtectedRoute />}>
                {/* 👇 todo lo protegido pasa por el wrapper que pinta overlays */}
                <Route element={<ProtectedOverlays />}>
                  {/* aterrizaje por rol */}
                  <Route index element={<RoleLanding />} />
                  {/* alias: /app/firmas/pendientes → /profesor/firmas */}
                  <Route
                    path="/app/firmas/pendientes"
                    element={<Navigate to="/profesor/firmas" replace />}
                  />

                  {/* página Vacaciones/Ausencias para CUALQUIERA salvo cliente */}
                  <Route
                    path="/vacaciones"
                    element={
                      <RequireRole
                        roles={[
                          'admin',
                          'admisiones',
                          'att_alumnado',
                          'att_familias',
                          'att_profesorado',
                          'hr',
                          'facturacion',
                          'consultor',
                          'profesor',
                        ]}
                      >
                        <VacacionesProfesorPage />
                      </RequireRole>
                    }
                  />

                  {/* cambio de contraseña forzado */}
                  <Route path="/change-password" element={<ChangePasswordPage />} />

                  {/* ===== CLIENTE ===== */}
                  <Route path="/cliente" element={<ClienteWrapper />}>
                    <Route index element={<Navigate to="agenda" replace />} />
                    <Route path="agenda" element={<ClienteAgendaPage />} />
                    <Route path="historial" element={<ClienteHistorialPage />} />
                    <Route path="cuenta" element={<ClienteCuentaPage />} />
                  </Route>

                  {/* ===== PROFESOR ===== */}
                  <Route path="/profesor" element={<RequireRole roles="profesor" />}>
                    <Route element={<ProfesorWrapper />}>
                      <Route index element={<ProfesorDashboardPage />} />
                      <Route index element={<Navigate to="agenda" replace />} />
                      <Route path="agenda" element={<ProfesorAgendaPage />} />
                      <Route path="historial" element={<ProfesorHistorialPage />} />
                      <Route path="guardias" element={<GuardiasCalendarPage />} />
                      <Route path="firmas" element={<MisFirmasPendientes />} />
                      <Route path="cuenta" element={<ProfesorCuentaPage />} />
                      <Route path="vacaciones" element={<VacacionesProfesorPage />} />
                      <Route path="documentos" element={<DocumentosProfesorPage />} />
                    </Route>
                  </Route>

                  {/* ====== STAFF: consultor ====== */}
                  <Route path="/consultor" element={<RequireRole roles="consultor" />}>
                    <Route element={<ConsultorLayout />}>
                      <Route index element={<Navigate to="agenda" replace />} />
                      <Route path="agenda" element={<AgendaPage />} />
                    </Route>
                  </Route>

                  {/* ====== STAFF: administrador ====== */}
                  <Route
                    element={
                      <RequireRole
                        roles={[
                          'admin',
                          'admisiones',
                          'att_alumnado',
                          'att_familias',
                          'att_profesorado',
                          'att_alumnado',
                          'hr',
                          'facturacion',
                        ]}
                      />
                    }
                  >
                    <Route element={<DashboardLayout />}>
                      {/* agenda */}
                      <Route path="/agenda" element={<AgendaPage />} />
                      <Route
                        path="/consulta-disponibilidad"
                        element={<ConsultaDisponibilidadPage />}
                      />
                      <Route
                        path="/consulta-reservas-pendientes"
                        element={<ConsultaReservasPendientesPage />}
                      />
                      <Route path="/formaciones/nueva" element={<FormularioFormacion />} />

                      {/* ⭐ NUEVO: Mis reuniones, SOLO STAFF */}
                      <Route
                        path="/mis-reuniones"
                        element={
                          <RequireRole
                            roles={[
                              'admin',
                              'admisiones',
                              'att_alumnado',
                              'att_familias',
                              'att_profesorado',
                              'hr',
                              'facturacion',
                            ]}
                          >
                            <MisReunionesPage />
                          </RequireRole>
                        }
                      />

                      {/* ⭐ NUEVO: Mis Formularios/Evaluaciones */}
                      <Route
                        path="/mis-formularios"
                        element={
                          <RequireRole
                            roles={[
                              'admin',
                              'admisiones',
                              'att_alumnado',
                              'att_familias',
                              'att_profesorado',
                              'hr',
                              'facturacion',
                              'consultor',
                            ]}
                          >
                            <MyForms />
                          </RequireRole>
                        }
                      />

                      <Route
                        path="/guardias"
                        element={<Navigate to="/admin/manager/guardias" replace />}
                      />

                      {/* ─── INFORMES ─── */}
                      <Route path="/informes" element={<ReportsRootLayout />}>
                        <Route path="profesores" element={<ProfReportsLayout />}>
                          <Route index element={<Navigate to="horas" replace />} />
                          <Route path="horas" element={<HorasProfesorPage />} />
                          <Route path="semanal" element={<HorasSemanalesPage />} />
                          <Route
                            path="horas-contratadas"
                            element={<HorasProfesoresContratadasPage />}
                          />
                          <Route path="alumnos" element={<AlumnosProfesorPage />} />
                          <Route path="incidencias" element={<IncidenciasProfesorPage />} />
                          <Route path="retrasos" element={<RetrasosProfesorPage />} />
                          <Route
                            path="one-minute-paper-incumplimiento"
                            element={<OneMinutePaperIncumplimientoPage />}
                          />
                          <Route path="asignaturas" element={<AsignaturasProfesorPage />} />
                          <Route path="ocupacion" element={<OcupacionProfesorPage />} />
                          <Route path="horarios" element={<HeatmapHorarioPage />} />
                          <Route path="total" element={<TotalesProfesorPage />} />
                          <Route path="A365" element={<BonusA365Page />} />
                          <Route path="high-perfomance" element={<HighPerformancePage />} />
                          <Route path="ausencias" element={<AusenciasPage />} />
                          <Route path="holydays" element={<VacacionesPage />} />
                          <Route path="formaciones" element={<FormacionesPage />} />
                          <Route path="prevision" element={<PrevisionPage />} />
                        </Route>

                        <Route path="clientes" element={<ClienteReportsLayout />}>
                          <Route index element={<Navigate to="semanal" replace />} />
                          <Route path="semanal" element={<HorasClientesSemanales />} />
                          <Route path="profesor" element={<ProfesoresAlumnoPage />} />
                          <Route path="one-minute-paper" element={<OneMinutePaperAlumnoPage />} />
                          <Route path="incidencias" element={<IncidenciasAlumnoPage />} />
                          <Route path="retrasos" element={<RetrasosAlumnoPage />} />
                          <Route path="progreso" element={<ProgresoAlumnoPage />} />
                          <Route path="proyecto" element={<AlumnosProyectoBarPage />} />
                          <Route path="total-unicos" element={<TotalAlumnosUnicosPage />} />
                          <Route path="cancelacion" element={<CancelacionAlumnoPage />} />
                          <Route path="evaluaciones" element={<ClienteEvaluacionesReportPage />} />
                          <Route path="altas-bajas" element={<AltasBajasReport />} />
                          <Route path="mensual" element={<HorasClientesMensuales />} />
                        </Route>

                        <Route path="reservas" element={<ReservasReportsLayout />}>
                          <Route index element={<Navigate to="metricas" replace />} />
                          <Route path="metricas" element={<ReservasMetricasPage />} />
                          <Route path="totales" element={<TotalReservasPage />} />
                          <Route path="canceladas" element={<CancelacionTasaPage />} />
                          <Route path="resheat" element={<HeatmapHorarioReservasPage />} />
                          <Route path="distribucion" element={<DistribucionReservasPage />} />
                          <Route path="progresion" element={<SeriesReservasPage />} />
                          <Route path="firmas" element={<FirmasDiaPage />} />
                          <Route path="retrasos" element={<RetrasosReservasPage />} />
                          <Route path="carga-formato" element={<CargaFormatoPage />} />
                          <Route
                            path="utilizacion-capacidad"
                            element={<UtilizacionCapacidadPage />}
                          />
                          <Route path="incidencias-tasa" element={<IncidenciasTasaPage />} />
                        </Route>

                        <Route path="asignaturas" element={<AsignaturasReportsLayout />}>
                          <Route index element={<Navigate to="semanal" replace />} />
                          <Route path="semanal" element={<HorasAsignaturasSemanalesPage />} />
                          <Route path="profesores" element={<AsignaturasProfesoresPage />} />
                          <Route path="demanda" element={<AsignaturasDemandaPage />} />
                          <Route path="alumnos-unicos" element={<AsignaturasAlumnosUnicosPage />} />
                          <Route path="ratio-profesor" element={<AsignaturasRatioProfesorPage />} />
                          <Route path="centro-formato" element={<AsignaturasCentroFormatoPage />} />
                        </Route>

                        <Route path="reuniones" element={<ReunionesReportsLayout />}>
                          <Route index element={<Navigate to="metricas" replace />} />
                          <Route path="metricas" element={<ReunionesMetricasPage />} />
                          <Route
                            path="seguimiento-servicio"
                            element={<SeguimientoServicioPage />}
                          />
                        </Route>

                        <Route path="centros" element={<CentrosReportsLayout />}>
                          <Route index element={<Navigate to="ocupacion" replace />} />
                          <Route path="ocupacion" element={<OcupacionCentroPage />} />
                        </Route>
                      </Route>

                      {/* reservas manuales */}
                      <Route path="/reservas/nueva" element={<NuevaReservaPage />} />

                      {/* —— bloque Manager —— */}
                      <Route path="/admin/manager" element={<ManagerLayout />}>
                        <Route index element={<Navigate to="profesor-datos" replace />} />
                        <Route path="guardias" element={<CuadroMensualPage />} />
                        {/* ===== AULAS / SALAS ===== */}
                        <Route path="aulas">
                          {/* Si navegan a /admin/manager/aulas → redirige a Centros */}
                          <Route index element={<Navigate to="../centros" replace />} />

                          {/* Gestión de aulas por centro */}
                          <Route path=":id" element={<AulasCentroPage />} />

                          {/* Planificador por centro (timeline DnD) */}
                          <Route path=":id/planificador" element={<PlanificadorCentroPage />} />
                        </Route>
                        <Route path="guardias/calendario" element={<GuardiasCalendarPage />} />
                        <Route path="profesor-datos" element={<ProfesorDatosPage />} />
                        <Route path="reuniones" element={<Navigate to="calendario" replace />} />
                        <Route path="reuniones/calendario" element={<CalendarioReunionesPage />} />
                        <Route path="reuniones/reglas" element={<ReunionReglasPage />} />
                        <Route
                          path="profesor-disponibilidad"
                          element={<ProfesorDisponibilidadPage />}
                        />
                        <Route path="profesor-vacaciones" element={<ProfesorVacacionesPage />} />
                        <Route path="staff-disponibilidad" element={<StaffDisponibilidadPage />} />
                        <Route path="documentos" element={<DocumentosPage />} />
                        {/* Ruta de métricas ANTES de formularios para que React Router la matchee primero */}
                        <Route
                          path="formularios/:id/metricas"
                          element={<FormulariosMetricasPage />}
                        />
                        <Route path="formularios" element={<FormulariosPage />} />
                        {/* Evaluaciones */}
                        <Route
                          path="evaluaciones/:id/metricas"
                          element={<EvaluacionesMetricasPage />}
                        />
                        <Route
                          path="evaluaciones/pendientes"
                          element={<EvaluacionesPendientesPage />}
                        />
                        <Route path="evaluaciones" element={<EvaluacionesPage />} />
                        {/* Historial de evaluaciones por usuario */}
                        <Route
                          path="usuarios/:usuarioId/evaluaciones"
                          element={<UsuarioEvaluacionesPage />}
                        />
                        <Route path="festivos" element={<FestivosPage />} />
                        <Route
                          path="usuarios"
                          element={createProtectedRoute(['hr', 'admin'], UsuariosPage)()}
                        />
                        <Route
                          path="audit-logs"
                          element={createProtectedRoute(['admin'], AuditLogViewer)()}
                        />
                        <Route path="asignaturas" element={<AsignaturasPage />} />
                        <Route path="centros" element={<CentrosPage />} />
                        <Route path="reunion-reglas" element={<ReunionReglasPage />} />
                        <Route path="clientes" element={<ClientesPage />} />
                        <Route path="reservas" element={<ReservasManagerLayout />}>
                          <Route index element={<Navigate to="proyectos" replace />} />
                          <Route path="proyectos" element={<ProyectosPage />} />
                          <Route path="estados" element={<EstadosReservaPage />} />
                        </Route>
                      </Route>
                      {/* alias para HR: /hr/vacaciones → página de manager */}
                      <Route
                        path="/hr/vacaciones"
                        element={<Navigate to="/admin/manager/profesor-vacaciones" replace />}
                      />
                    </Route>
                  </Route>
                </Route>
              </Route>

              {/* fallback → login */}
              <Route path="*" element={<Navigate to="/login" replace />} />
            </Routes>
          </BrowserRouter>
        </BroadcastModalProvider>
      </LocalizationProvider>
    </ThemeProvider>
  );
}
